import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, KeyRound } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/applications", label: "Nos activités" },
  { to: "/realisations", label: "Nos réalisations" },
  { to: "/master-tg", label: "Master TG" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
  { to: "/locataire", label: "Espace locataire" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const darkHero = pathname === "/" || /^\/applications\/.+/.test(pathname);
  const solid = scrolled || open || !darkHero;
  const overHero = !solid; // transparent bar over a dark hero, at the top

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <Logo className="h-9 w-9" light={overHero} />
          <span className={`font-display font-bold text-lg tracking-tight leading-none ${overHero ? "text-white" : "text-navy"}`}>
            TIRERA <span className={overHero ? "text-white/70 font-semibold" : "text-navy/60 font-semibold"}>GROUP</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                overHero
                  ? pathname === l.to
                    ? "text-white"
                    : "text-white/70 hover:text-white"
                  : pathname === l.to
                  ? "text-navy"
                  : "text-muted-foreground hover:text-navy"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/applications"
            data-testid="nav-cta-button"
            className={`ml-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-colors ${
              overHero ? "bg-white text-navy hover:bg-white/90" : "bg-navy text-white hover:bg-navy-600"
            }`}
          >
            Découvrir
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden p-2 ${overHero ? "text-white" : "text-navy"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-border bg-white"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              <Link
                to="/locataire"
                data-testid="mobile-nav-espace-locataire"
                className="mb-2 flex items-center justify-center gap-2 px-4 py-3 text-base font-semibold text-white bg-navy rounded-xl hover:bg-navy-600"
              >
                <KeyRound size={18} /> Espace locataire
              </Link>
              {LINKS.filter((l) => l.to !== "/locataire").map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|à/g, "-")}`}
                  className="px-4 py-3 text-base font-medium text-navy rounded-lg hover:bg-secondary"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
